/**
 * The field-app primer: one set of facts, two renderings.
 *
 * **The content is JSON, not this file.** That is deliberate and it is the
 * whole point of the module. The primer is published twice, as an in-app
 * walkthrough and as a PDF, and the usual way a help document goes wrong is
 * that somebody corrects one copy. Keeping the words in a data file that both
 * renderers read makes drift structurally impossible rather than a thing
 * somebody has to remember.
 *
 * **Why JSON rather than a `.ts` module.** The PDF is built by a plain Node
 * script, and the repo has no TypeScript runner for scripts. JSON is the one
 * format the React app and a bare `node` process both read with no tooling at
 * all. The cost is that the content file cannot carry comments, which is why
 * the reasoning lives here instead.
 *
 * Where the facts come from: the device-testing section of `CLAUDE.md`, which
 * is the accumulated cost of several round trips with a real iPhone, and the
 * screens themselves. The seven home cards and their hints are quoted from
 * `features/Field/screens/FieldHome.tsx` so the primer describes the screen
 * that exists rather than the one it was written against.
 */

import doc from './fieldPrimer.json'

/** Which phone a set of instructions applies to. */
export type PrimerPlatform = 'ios' | 'android' | 'all'

/**
 * A callout is a claim that does not belong in the flow of the steps.
 *
 * - `warning` is a thing that will cost the reader time if they skip it.
 * - `tip` is a way out of a corner.
 * - `why` is the reason behind an instruction, kept separate so a reader in a
 *   hurry can follow the steps and a reader who is stuck can find out what the
 *   step was actually for.
 */
export interface PrimerCallout {
	kind: 'warning' | 'tip' | 'why'
	title: string
	text: string
}

/** A literal sequence of taps, for one kind of phone. */
export interface PrimerActions {
	platform: PrimerPlatform
	/** Shown as the heading above the list, e.g. "iPhone, in Safari". */
	label: string
	items: string[]
}

/** A named thing with a one-line description, e.g. a home-screen card. */
export interface PrimerListItem {
	label: string
	hint: string
}

export interface PrimerStep {
	id: string
	title: string
	/** Paragraphs. Plain text: no markup, because two renderers would have to agree on it. */
	body?: string[]
	actions?: PrimerActions[]
	callouts?: PrimerCallout[]
	list?: PrimerListItem[]
}

/** A chapter. One section can hold several steps. */
export interface PrimerSection {
	id: string
	title: string
	summary?: string
	steps: PrimerStep[]
}

export interface PrimerTrouble {
	symptom: string
	cause: string
	fix: string
}

export interface PrimerDoc {
	id: string
	/** Bumped when the content changes. Printed on the PDF so a paper copy can be dated. */
	version: string
	updated: string
	title: string
	subtitle: string
	audience: string
	estimatedMinutes: number
	appUrl: string
	intro: string[]
	sections: PrimerSection[]
	troubleshooting: PrimerTrouble[]
	closing: string[]
}

export const fieldPrimer = doc as PrimerDoc

/**
 * The walkthrough shows one step per page, but the steps are grouped into
 * sections for the PDF's benefit. Flattening here, once, keeps the paging in
 * the dialog from having to know about the nesting, and keeps the two
 * renderers reading the same array in the same order.
 *
 * Troubleshooting is NOT flattened in. It is a lookup table rather than a step
 * to walk through, so both renderers place it at the end in their own way.
 */
export interface PrimerPage extends PrimerStep {
	sectionId: string
	sectionTitle: string
}

export const primerPages = (primer: PrimerDoc = fieldPrimer): PrimerPage[] =>
	primer.sections.flatMap(section =>
		section.steps.map(step => ({
			...step,
			sectionId: section.id,
			sectionTitle: section.title,
		})),
	)

export default fieldPrimer
